import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Icon, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActualAuctionStatusEnum } from "../../../_common/enum/actualAuctionStatus";
import { AuctionStatusEnum } from "../../../_common/enum/auctionStatus.enum";
import { OnlineStatusEnum } from "../../../_common/enum/onlineStatus.enum";
import { getUserPresence, presenceLabel } from "../../../_common/auctionPresence.helper";
import { MessageNotification } from "../../../_components/box/message-notification";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardPanel } from "../../../_components/panel/standard.panel";
import { useAuctionUserAdmin } from "../../../_hubs/auctionUserAdmin.hub";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { accountService } from "../../../_services/accounts.service";
import { auctionService } from "../../../_services/auctionService.service";
import { catalogueService } from "../../../_services/catalogueService.service";
import { catalogueUserService } from "../../../_services/catalogueUser.service";
import { AuctionChat } from "../../auction/components/auction-chat";
import { AuctionMessages } from "../components/auction-messages";
import { CatalogueItemStatusCard } from "../components/auction-catalogueItemStatusCard";
import { AuctionUserList } from "../components/auction-userList";
import { AuctionAnnualCountdown } from "./auction-annual-countdown";
import { AuctionAnnualBasketHistoryList } from "./auction-annual-basket-history-list";
import { AuctionAnnualCataloguePanel } from "./auction-annual-catalogue-panel";
import {
    AnnualCatalogueItemRow,
    AnnualBauRestrictionRow,
    AnnualUserRow,
    AuctionAnnualStateResponse,
    AuctionAnnualTurnResponse,
    BasketHistoryEntry,
    buildAnnualCatalogueItemMap,
    getAnnualActualStatus,
    reduceAnnualTurnHistory,
    sortAnnualCatalogueItems,
} from "./auction-annual-shared";

type TurnActionType = "complete" | "accept" | "skip";
type AnnualAdvanceAction = 1 | 2 | 3;

function presenceColor(presence: OnlineStatusEnum) {
    if (presence === OnlineStatusEnum.Online) return "success.main";
    if (presence === OnlineStatusEnum.Away) return "warning.main";
    return "error.main";
}

export function AuctionAnnualRoot(params: { auction?: any }) {
    const { onlineUsers, heartbeats, tabVisibleByUserId } = useAuctionUserAdmin();
    const isAdminUser = accountService.isAdmin;
    const isMonitorUser = accountService.isMonitor;
    const isCustomerUser = accountService.isCustomer;
    // Monitors reuse this screen read-only (same idea as adhoc monitor): full visibility, no controls.
    // Admins oversee only — bidders select and accept/skip on their own screen.
    const canSelectItems = false;
    const currentAuthUserId = String(accountService.currentUserValue?.id ?? "");
    const [loading, setLoading] = useState({ catalogue: true, users: true });
    const [catalogues, setCatalogues] = useState<any[]>([]);
    const [users, setUsers] = useState<AnnualUserRow[]>([]);
    const [activeCatalogueId, setActiveCatalogueId] = useState<number | null>(null);
    const [roundNumber, setRoundNumber] = useState(1);
    const [annualRoundLimit, setAnnualRoundLimit] = useState(12);
    const [lastRoundExtraAllowance, setLastRoundExtraAllowance] = useState(500);
    const [currentTurnIndex, setCurrentTurnIndex] = useState(0);
    const [carryOverByUserId, setCarryOverByUserId] = useState<Record<number, number>>({});
    const [basketByUserId, setBasketByUserId] = useState<Record<number, number[]>>({});
    const [basketHistoryByUserId, setBasketHistoryByUserId] = useState<Record<number, BasketHistoryEntry[]>>({});
    const [pendingTurnItemIds, setPendingTurnItemIds] = useState<number[]>([]);
    const [basketDialogUserId, setBasketDialogUserId] = useState<number | null>(null);
    const [annualTimerStatus, setAnnualTimerStatus] = useState(getAnnualActualStatus(Number(params.auction?.auctionStatusId ?? 0)));
    const [timerAnchors, setTimerAnchors] = useState<Pick<
        AuctionAnnualStateResponse,
        "timerDurationSeconds" | "timerEndsAtUtc" | "timerRemainingSeconds"
    > | null>(null);
    const [turnRequestLoading, setTurnRequestLoading] = useState(false);
    const [turnActionConfirm, setTurnActionConfirm] = useState<{
        type: TurnActionType;
        skipOnly: boolean;
    } | null>(null);

    function applyAnnualStateFromServer(state: AuctionAnnualStateResponse | null, orderedUsers: AnnualUserRow[]) {
        if (!state) return;

        const serverRound = Number(state.currentRoundNumber ?? 1);
        const serverCurrentUserId = Number(state.currentCatalogueUserId ?? 0);
        const serverIndex = orderedUsers.findIndex((u) => u.id === serverCurrentUserId);

        setRoundNumber(serverRound > 0 ? serverRound : 1);
        setCurrentTurnIndex(serverIndex >= 0 ? serverIndex : 0);
        setAnnualRoundLimit(Math.min(12, Math.max(1, Number(state.maxRounds ?? 12))));
        setLastRoundExtraAllowance(Math.max(0, Number(state.lastRoundExtraAllowance ?? 500)));
        setTimerAnchors({
            timerDurationSeconds: state.timerDurationSeconds,
            timerEndsAtUtc: state.timerEndsAtUtc,
            timerRemainingSeconds: state.timerRemainingSeconds,
        });
    }

    function applyAnnualTurnHistoryFromServer(turns: AuctionAnnualTurnResponse[], clearPendingSelection = true) {
        const historyState = reduceAnnualTurnHistory(turns);
        setBasketByUserId(historyState.basketByUserId);
        setBasketHistoryByUserId(historyState.basketHistoryByUserId);
        setCarryOverByUserId(historyState.carryOverByUserId);
        if (clearPendingSelection) {
            setPendingTurnItemIds([]);
        }
    }

    async function refreshAnnualRealtime(auctionId: number) {
        try {
            const latestCatalogues = await catalogueService.getAllAuction(auctionId).catch(() => []);
            const safeCatalogues = Array.isArray(latestCatalogues) ? latestCatalogues : [];
            if (safeCatalogues.length > 0) {
                setCatalogues(safeCatalogues);
                const activeId = Number((safeCatalogues.find((c: any) => c?.isActive) ?? safeCatalogues[0])?.id ?? 0);
                if (activeId > 0) setActiveCatalogueId(activeId);
            }

            const annualState = await auctionService.getAnnualState(auctionId).catch(() => null);
            const annualTurns = await auctionService.getAnnualTurnHistory(auctionId).catch(() => []);
            const currentUserId = Number(currentTurnUser?.id ?? 0);
            const nextUserId = Number(annualState?.currentCatalogueUserId ?? 0);
            const nextRound = Number(annualState?.currentRoundNumber ?? roundNumber);
            const turnChanged = currentUserId !== nextUserId || Number(roundNumber) !== nextRound;

            applyAnnualTurnHistoryFromServer(annualTurns, false);
            applyAnnualStateFromServer(annualState, users);
            if (turnChanged) {
                setPendingTurnItemIds([]);
            }
        } catch (err) {
            handleErrorResponse(err);
        }
    }

    useEffect(() => {
        if (!(params.auction?.id > 0)) {
            setCatalogues([]);
            setUsers([]);
            setActiveCatalogueId(null);
            setRoundNumber(1);
            setAnnualRoundLimit(12);
            setLastRoundExtraAllowance(500);
            setCurrentTurnIndex(0);
            setCarryOverByUserId({});
            setBasketByUserId({});
            setBasketHistoryByUserId({});
            setPendingTurnItemIds([]);
            setAnnualTimerStatus(getAnnualActualStatus(AuctionStatusEnum.Pending));
            setTimerAnchors(null);
            setTurnActionConfirm(null);
            setLoading({ catalogue: false, users: false });
            return;
        }

        loadAnnualReadOnly(params.auction.id);
    // loadAnnualReadOnly is intentionally not a dependency to avoid reload loops on function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.auction?.id]);

    useEffect(() => {
        setAnnualTimerStatus(getAnnualActualStatus(Number(params.auction?.auctionStatusId ?? 0)));
        if (params.auction?.id > 0 && users.length > 0) {
            refreshAnnualRealtime(params.auction.id);
        }
        // refreshAnnualRealtime intentionally omitted from deps; trigger only on status stream changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.auction?.auctionStatusId]);

    async function loadAnnualReadOnly(auctionId: number) {
        setLoading({ catalogue: true, users: true });
        try {
            const loadedCatalogues = await catalogueService.getAllAuction(auctionId);
            const allCatalogues = Array.isArray(loadedCatalogues) ? loadedCatalogues : [];
            setCatalogues(allCatalogues);

            const selectedCatalogue =
                allCatalogues.find((c: any) => c?.isActive) ??
                allCatalogues[0] ??
                null;

            const selectedCatalogueId = Number(selectedCatalogue?.id ?? 0);
            setActiveCatalogueId(selectedCatalogueId > 0 ? selectedCatalogueId : null);
            setLoading((prev) => ({ ...prev, catalogue: false }));

            if (!(selectedCatalogueId > 0)) {
                setUsers([]);
                setLoading((prev) => ({ ...prev, users: false }));
                return;
            }

            const loadedUsers = await catalogueUserService.getAll(selectedCatalogueId);
            const orderedUsers = (Array.isArray(loadedUsers) ? loadedUsers : [])
                .slice()
                .sort((a: AnnualUserRow, b: AnnualUserRow) => {
                    const left = a.sequenceOrder ?? Number.MAX_SAFE_INTEGER;
                    const right = b.sequenceOrder ?? Number.MAX_SAFE_INTEGER;
                    if (left !== right) return left - right;
                    return (a.id ?? 0) - (b.id ?? 0);
                });

            setUsers(orderedUsers);
            setRoundNumber(1);
            setCurrentTurnIndex(0);
            setCarryOverByUserId({});
            setBasketByUserId({});
            setBasketHistoryByUserId({});
            setPendingTurnItemIds([]);
            setTurnActionConfirm(null);
            const annualState = await auctionService.getAnnualState(auctionId).catch(() => null);
            const annualTurns = await auctionService.getAnnualTurnHistory(auctionId).catch(() => []);
            applyAnnualTurnHistoryFromServer(annualTurns);
            applyAnnualStateFromServer(annualState, orderedUsers);
            setLoading((prev) => ({ ...prev, users: false }));
        } catch (err) {
            handleErrorResponse(err);
            setLoading({ catalogue: false, users: false });
        }
    }

    const activeCatalogue = useMemo(
        () => catalogues.find((c: any) => Number(c?.id) === Number(activeCatalogueId)),
        [catalogues, activeCatalogueId],
    );

    const catalogueItems = useMemo<AnnualCatalogueItemRow[]>(
        () => sortAnnualCatalogueItems(activeCatalogue?.catalogueItems),
        [activeCatalogue],
    );

    const catalogueItemsById = useMemo(() => {
        return buildAnnualCatalogueItemMap(catalogueItems);
    }, [catalogueItems]);

    function normalizeBauCode(code: string | null | undefined) {
        return String(code ?? "").trim().toUpperCase();
    }

    function getUserBauLimitMap(user: AnnualUserRow | null | undefined) {
        const map: Record<string, number> = {};
        (user?.bauRestrictions ?? []).forEach((row: AnnualBauRestrictionRow) => {
            const code = normalizeBauCode(row?.bauCode);
            const qty = Number(row?.quantity ?? 0);
            if (!code || !(qty > 0)) return;
            map[code] = qty;
        });
        return map;
    }

    const countBauUsage = useCallback((itemIds: number[]) => {
        const map: Record<string, number> = {};
        itemIds.forEach((itemId) => {
            const row = catalogueItemsById.get(itemId);
            const code = normalizeBauCode(row?.forestCode);
            if (!code) return;
            const volume = Number(row?.totalVolume ?? 0);
            if (!(volume > 0)) return;
            map[code] = (map[code] ?? 0) + volume;
        });
        return map;
    }, [catalogueItemsById]);

    const currentTurnUser = users[currentTurnIndex] ?? null;
    const currentClientUser = users.find((u) => String(u.userId ?? "") === currentAuthUserId) ?? null;
    const isCurrentClientTurn = !!currentClientUser && !!currentTurnUser && currentClientUser.id === currentTurnUser.id;
    const visibleUsers = isCustomerUser ? (currentClientUser ? [currentClientUser] : []) : users;

    const committedItemIds = useMemo(
        () => Object.values(basketByUserId).flat(),
        [basketByUserId],
    );

    const occupiedItemIds = useMemo(
        () => new Set<number>([...committedItemIds, ...pendingTurnItemIds]),
        [committedItemIds, pendingTurnItemIds],
    );

    const pendingTurnVolume = useMemo(
        () =>
            pendingTurnItemIds.reduce((total, itemId) => {
                const volume = Number(catalogueItemsById.get(itemId)?.totalVolume ?? 0);
                return total + (Number.isFinite(volume) ? volume : 0);
            }, 0),
        [pendingTurnItemIds, catalogueItemsById],
    );

    const committedBauUsageByUserId = useMemo(() => {
        const map: Record<number, Record<string, number>> = {};
        Object.entries(basketByUserId).forEach(([rawUserId, ids]) => {
            const userId = Number(rawUserId);
            map[userId] = countBauUsage(ids ?? []);
        });
        return map;
    }, [basketByUserId, countBauUsage]);

    const pendingBauUsageForCurrentUser = useMemo(
        () => countBauUsage(pendingTurnItemIds),
        [pendingTurnItemIds, countBauUsage],
    );

    const currentUserBaseVolume = Number(currentTurnUser?.volumePerRound ?? 0);
    const currentUserCarryOver = Number(
        currentTurnUser ? carryOverByUserId[currentTurnUser.id] ?? 0 : 0,
    );
    const isLastRound = roundNumber >= annualRoundLimit;
    const currentUserAvailableVolume = Math.max(0, currentUserBaseVolume + currentUserCarryOver + (isLastRound ? lastRoundExtraAllowance : 0));
    const currentUserRemainingVolume = Math.max(0, currentUserAvailableVolume - pendingTurnVolume);
    const hasPendingSelection = pendingTurnItemIds.length > 0;
    const currentUserBauLimitMap = getUserBauLimitMap(currentTurnUser);
    const currentUserCommittedBauUsage = currentTurnUser ? (committedBauUsageByUserId[currentTurnUser.id] ?? {}) : {};
    const currentUserBauLeftMap: Record<string, number> = Object.keys(currentUserBauLimitMap).reduce((acc, code) => {
        const limit = currentUserBauLimitMap[code] ?? 0;
        const used = (currentUserCommittedBauUsage[code] ?? 0) + (pendingBauUsageForCurrentUser[code] ?? 0);
        acc[code] = Math.max(0, limit - used);
        return acc;
    }, {} as Record<string, number>);
    function getUserBauLeftEntries(user: AnnualUserRow, includePendingSelection = false) {
        const limitMap = getUserBauLimitMap(user);
        const committedUsageMap = committedBauUsageByUserId[user.id] ?? {};
        const pendingUsageMap =
            includePendingSelection && currentTurnUser?.id === user.id
                ? pendingBauUsageForCurrentUser
                : {};

        return Object.keys(limitMap)
            .sort()
            .map((code) => {
                const limit = limitMap[code] ?? 0;
                const used = (committedUsageMap[code] ?? 0) + (pendingUsageMap[code] ?? 0);
                const left = Math.max(0, limit - used);
                const ratio = limit > 0 ? left / limit : 1;
                return {
                    code,
                    left,
                    limit,
                    isLow: ratio < 0.2,
                };
            });
    }

    const currentUserBauLeftEntries = currentTurnUser ? getUserBauLeftEntries(currentTurnUser, true) : [];

    function renderBauLeftInline(entries: { code: string; left: number; isLow: boolean }[]) {
        if (entries.length === 0) return null;
        return (
            <>
                {entries.map((entry, index) => (
                    <Box
                        key={`bau-left-${entry.code}`}
                        component="span"
                        sx={{ color: entry.isLow ? "error.main" : "inherit", fontWeight: entry.isLow ? 700 : 400 }}
                    >
                        {index > 0 ? ", " : ""}
                        {entry.code}: {entry.left}
                    </Box>
                ))}
            </>
        );
    }

    function getItemVolume(itemId: number): number {
        return Number(catalogueItemsById.get(itemId)?.totalVolume ?? 0);
    }

    function togglePendingItem(itemId: number) {
        if (!canSelectItems) return;
        if (annualTimerStatus === ActualAuctionStatusEnum.NotStarted) return;
        if (!currentTurnUser) return;

        const isAlreadySelected = pendingTurnItemIds.includes(itemId);
        if (isAlreadySelected) {
            setPendingTurnItemIds((prev) => prev.filter((id) => id !== itemId));
            return;
        }

        if (committedItemIds.includes(itemId)) return;

        const itemVolume = getItemVolume(itemId);
        if (itemVolume <= 0) return;

        if (pendingTurnVolume + itemVolume > currentUserAvailableVolume) {
            return;
        }

        const itemBauCode = normalizeBauCode(catalogueItemsById.get(itemId)?.forestCode);
        const itemBauLimit = currentUserBauLimitMap[itemBauCode];
        if (itemBauCode && itemBauLimit > 0) {
            const used = (currentUserCommittedBauUsage[itemBauCode] ?? 0) + (pendingBauUsageForCurrentUser[itemBauCode] ?? 0);
            if ((used + itemVolume) > itemBauLimit) {
                return;
            }
        }

        setPendingTurnItemIds((prev) => [...prev, itemId]);
    }

    function getUserBasketVolume(userId: number) {
        return (basketByUserId[userId] ?? []).reduce(
            (total, itemId) => total + getItemVolume(itemId),
            0,
        );
    }

    function getRoundUsage(user: AnnualUserRow) {
        const currentRoundEntries = (basketHistoryByUserId[user.id] ?? []).filter(
            (entry) => entry.roundNumber === roundNumber,
        );
        const currentRoundHistory = currentRoundEntries.length > 0
            ? currentRoundEntries[currentRoundEntries.length - 1]
            : undefined;

        if (currentTurnUser?.id === user.id) {
            if (pendingTurnItemIds.length > 0) {
                return {
                    selectedItemIds: pendingTurnItemIds,
                    allowance: currentUserAvailableVolume,
                    used: pendingTurnVolume,
                    remaining: currentUserRemainingVolume,
                };
            }

            if (currentRoundHistory) {
                return {
                    selectedItemIds: currentRoundHistory.itemIds,
                    allowance: currentRoundHistory.allowance,
                    used: currentRoundHistory.used,
                    remaining: currentRoundHistory.remaining,
                };
            }

            return {
                selectedItemIds: [],
                allowance: currentUserAvailableVolume,
                used: 0,
                remaining: currentUserAvailableVolume,
            };
        }

        if (currentRoundHistory) {
            return {
                selectedItemIds: currentRoundHistory.itemIds,
                allowance: currentRoundHistory.allowance,
                used: currentRoundHistory.used,
                remaining: currentRoundHistory.remaining,
            };
        }

        const allowance = Number(user.volumePerRound ?? 0) + Number(carryOverByUserId[user.id] ?? 0) + (isLastRound ? lastRoundExtraAllowance : 0);
        return {
            selectedItemIds: [],
            allowance,
            used: 0,
            remaining: allowance,
        };
    }

    async function completeTurnServer(skipOnly: boolean) {
        if (!params.auction?.id || !currentTurnUser) return false;

        setTurnRequestLoading(true);
        try {
            const selectedCatalogueItemIds = skipOnly ? [] : pendingTurnItemIds;
            await auctionService.completeAnnualTurn(params.auction.id, currentTurnUser.id, selectedCatalogueItemIds);
            const annualTurns = await auctionService.getAnnualTurnHistory(params.auction.id).catch(() => []);
            applyAnnualTurnHistoryFromServer(annualTurns);
            const annualState = await auctionService.getAnnualState(params.auction.id).catch(() => null);
            applyAnnualStateFromServer(annualState, users);
            setAnnualTimerStatus(getAnnualActualStatus(AuctionStatusEnum.Pending));
            return true;
        } catch (err) {
            handleErrorResponse(err);
            return false;
        } finally {
            setTurnRequestLoading(false);
        }
    }

    async function advanceTurnServer(action: AnnualAdvanceAction) {
        if (!params.auction?.id) return;

        setTurnRequestLoading(true);
        try {
            const state = await auctionService.advanceAnnualTurn(params.auction.id, action);
            const annualTurns = await auctionService.getAnnualTurnHistory(params.auction.id).catch(() => []);
            applyAnnualTurnHistoryFromServer(annualTurns);
            applyAnnualStateFromServer(state, users);
            setAnnualTimerStatus(
                getAnnualActualStatus(action === 3 ? AuctionStatusEnum.Completed : AuctionStatusEnum.Active),
            );
        } catch (err) {
            handleErrorResponse(err);
        } finally {
            setTurnRequestLoading(false);
        }
    }

    const basketDialogUser = users.find((u) => u.id === basketDialogUserId) ?? null;
    const isAnnualTimerRunning =
        annualTimerStatus === ActualAuctionStatusEnum.Active ||
        annualTimerStatus === ActualAuctionStatusEnum.ReOffered ||
        annualTimerStatus === ActualAuctionStatusEnum.ClosingShortly;
    const isPendingForTurnAdvance = annualTimerStatus === getAnnualActualStatus(AuctionStatusEnum.Pending);
    // Auto-paused because the current-turn customer went offline. The admin must still be able to
    // drive the turn (resume the timer or advance) - being offline should never trap the auction.
    const isAuctionDisconnected = annualTimerStatus === ActualAuctionStatusEnum.UserDisconnected;
    const isAuctionCompleted = annualTimerStatus === ActualAuctionStatusEnum.Completed;
    const isAuctionStarted = annualTimerStatus !== ActualAuctionStatusEnum.NotStarted;
    const isLastCustomerInRound = users.length > 0 && currentTurnIndex === users.length - 1;
    // Who takes the turn after the current one, so the admin knows before advancing.
    const nextTurnUser = users.length > 0 ? (isLastCustomerInRound ? users[0] : users[currentTurnIndex + 1]) : null;
    const nextTurnRound = isLastCustomerInRound ? roundNumber + 1 : roundNumber;
    const nextTurnUserPresence = nextTurnUser
        ? getUserPresence({ userId: nextTurnUser.userId, onlineUsers, lastSeenByUserId: heartbeats, tabVisibleByUserId })
        : null;
    // The current customer has taken (or skipped) their turn this round once a turn record exists
    // for them in the current round. Until then the admin still needs Start Timer to run this turn;
    // after it, the only forward action is advancing to the next customer (which restarts the timer).
    const currentTurnUserActedThisRound = !!currentTurnUser &&
        (basketHistoryByUserId[currentTurnUser.id] ?? []).some((entry) => entry.roundNumber === roundNumber);
    const canAdvanceTurn = (isPendingForTurnAdvance || isAuctionDisconnected) && currentTurnUserActedThisRound;
    const canCompleteAuction = canAdvanceTurn && isLastCustomerInRound && isLastRound;
    const canClientAct = isCustomerUser && isCurrentClientTurn && !isPendingForTurnAdvance && annualTimerStatus === ActualAuctionStatusEnum.Active;
    const turnActionTitle =
        turnActionConfirm?.type === "accept"
            ? "Accept Selection"
            : turnActionConfirm?.type === "skip"
                ? "Skip Turn"
                : "Complete Turn";

    return (
        <>
            {isLastRound ? (
                <Grid2 size={{ xs: 12 }}>
                    <Typography textAlign={"center"} color="warning.main" variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                        It is the last round ({annualRoundLimit}). Extra allowance +{lastRoundExtraAllowance} is applied.
                    </Typography>
                </Grid2>
            ) : null}

            <Grid2 size={{ xs: 12, md: 4 }}>
                <AuctionAnnualCataloguePanel
                    loading={loading.catalogue}
                    hasActiveCatalogue={Boolean(activeCatalogue)}
                    catalogueItems={catalogueItems}
                    pendingTurnItemIds={pendingTurnItemIds}
                    committedItemIds={committedItemIds}
                    occupiedItemIds={occupiedItemIds}
                    isItemDisabled={() => !canSelectItems || !isAuctionStarted || isAuctionCompleted || !currentTurnUser || isPendingForTurnAdvance || (isCustomerUser && !isCurrentClientTurn)}
                    isItemTooLarge={(item, itemId) => !pendingTurnItemIds.includes(itemId) && Boolean(currentTurnUser) && Number(item.totalVolume ?? 0) > currentUserRemainingVolume}
                    isItemBauLimited={(item, itemId) => {
                        if (pendingTurnItemIds.includes(itemId)) return false;
                        const code = normalizeBauCode(item.forestCode);
                        const limit = currentUserBauLimitMap[code];
                        if (!code || !(limit > 0)) return false;
                        const itemVolume = Number(item.totalVolume ?? 0);
                        if (!(itemVolume > 0)) return false;
                        return (currentUserBauLeftMap[code] ?? limit) < itemVolume;
                    }}
                    getItemBauRemaining={(item) => {
                        const code = normalizeBauCode(item.forestCode);
                        const limit = currentUserBauLimitMap[code];
                        if (!code || !(limit > 0)) return null;
                        return currentUserBauLeftMap[code] ?? limit;
                    }}
                    onToggleItem={togglePendingItem}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 8 }}>
                <Grid2 container spacing={1}>
                    <Grid2 size={{ xs: 12 }}>
                        <CatalogueItemStatusCard actualAuctionStatusId={annualTimerStatus} isAnnual />
                    </Grid2>

                    {isAdminUser &&
                        <Grid2 size={{ xs: 12 }}>
                            {!isAuctionStarted ? (
                                <StandardButton
                                    icon="check"
                                    variant="success"
                                    disabled={!params.auction?.id || turnRequestLoading}
                                    onClick={() =>
                                        auctionService
                                            .startAnnual(params.auction.id)
                                            .then(() => {
                                                setAnnualTimerStatus(getAnnualActualStatus(AuctionStatusEnum.Pending));
                                                return refreshAnnualRealtime(params.auction.id);
                                            })
                                            .catch(handleErrorResponse)
                                    }
                                >
                                    Start Auction
                                </StandardButton>
                            ) : (
                                <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(0, 1fr))" } }}>
                                    <StandardButton
                                        icon="access_time"
                                        variant="success"
                                        disabled={isAuctionCompleted || isAnnualTimerRunning || currentTurnUserActedThisRound || !params.auction?.id || turnRequestLoading}
                                        onClick={() =>
                                            auctionService
                                                .setStatus(params.auction.id, AuctionStatusEnum.Active)
                                                .then(() => {
                                                    setAnnualTimerStatus(ActualAuctionStatusEnum.Active);
                                                    return refreshAnnualRealtime(params.auction.id);
                                                })
                                                .catch(handleErrorResponse)
                                        }
                                    >
                                        Start Timer
                                    </StandardButton>
                                    <StandardButton
                                        icon="block"
                                        variant="warning"
                                        disabled={isAuctionCompleted || !isAnnualTimerRunning || !params.auction?.id || turnRequestLoading}
                                        onClick={() =>
                                            auctionService
                                                .setStatus(params.auction.id, AuctionStatusEnum.Paused)
                                                .then(() => {
                                                    setAnnualTimerStatus(ActualAuctionStatusEnum.Paused);
                                                    return refreshAnnualRealtime(params.auction.id);
                                                })
                                                .catch(handleErrorResponse)
                                        }
                                    >
                                        Pause Timer
                                    </StandardButton>
                                    <StandardButton
                                        icon="restart_alt"
                                        variant="info"
                                        disabled={isAuctionCompleted || !params.auction?.id || turnRequestLoading}
                                        onClick={() =>
                                            auctionService
                                                .setStatus(params.auction.id, AuctionStatusEnum.Pending)
                                                .then(() => {
                                                    setAnnualTimerStatus(ActualAuctionStatusEnum.Pending);
                                                    return refreshAnnualRealtime(params.auction.id);
                                                })
                                                .catch(handleErrorResponse)
                                        }
                                    >
                                        Reset Timer
                                    </StandardButton>
                                    <StandardButton
                                        icon={canAdvanceTurn ? "restart_alt" : "check"}
                                        variant={canCompleteAuction ? "warning" : canAdvanceTurn ? "success" : "primary"}
                                        disabled={isAuctionCompleted || !currentTurnUser || turnRequestLoading}
                                        onClick={() =>
                                            canAdvanceTurn
                                                ? advanceTurnServer(isLastCustomerInRound ? (isLastRound ? 3 : 2) : 1)
                                                : setTurnActionConfirm({ type: "complete", skipOnly: false })
                                        }
                                    >
                                        {canAdvanceTurn
                                            ? isLastCustomerInRound
                                                ? isLastRound
                                                    ? "Complete Auction"
                                                    : "Start New Round"
                                                : "Next Customer"
                                            : "Complete Turn"}
                                    </StandardButton>
                                </Box>
                            )}
                        </Grid2>
                    }

                    <Grid2 size={{ xs: 9 }} sx={{ minWidth: 0 }}>
                        <Grid2 container spacing={1}>
                            {!isMonitorUser ? (
                            <Grid2 size={{ xs: 12 }}>
                                <StandardPanel
                                    title="Turn Order"
                                    loading={loading.users}
                                    height="260px"
                                >
                                    {!loading.users && users.length === 0 ? (
                                        <MessageNotification message="No registered users found for the active catalogue." />
                                    ) : (
                                        <>
                                            <Box sx={{ mb: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Round {roundNumber}
                                                </Typography>
                                                {!currentTurnUser ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Waiting for users...
                                                    </Typography>
                                                ) : isCustomerUser && !currentClientUser ? (
                                                    <MessageNotification message="You are not registered on the active annual catalogue." />
                                                ) : (
                                                    <>
                                                        {isCustomerUser ? (
                                                            <>
                                                                <Typography variant="body2">
                                                                    Current turn: <strong>{currentTurnUser.sequenceOrder ?? "—"}. {currentTurnUser.userName ?? currentTurnUser.userId}</strong>
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {isCurrentClientTurn ? "It is your turn." : "Waiting for your turn."}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Your allowance: {currentClientUser?.volumePerRound ?? 0} | In this turn selected: {isCurrentClientTurn ? pendingTurnVolume : 0}
                                                                </Typography>
                                                                <Box sx={{ mt: 1, display: "grid", gap: 1 }}>
                                                                    <StandardButton
                                                                        icon={hasPendingSelection ? "check" : "restart_alt"}
                                                                        variant={hasPendingSelection ? "success" : "info"}
                                                                        disabled={!canClientAct || turnRequestLoading}
                                                                        onClick={() =>
                                                                            setTurnActionConfirm({
                                                                                type: hasPendingSelection ? "accept" : "skip",
                                                                                skipOnly: !hasPendingSelection,
                                                                            })
                                                                        }
                                                                    >
                                                                        {hasPendingSelection ? "Accept Selection" : "Skip Turn (Carry Over)"}
                                                                    </StandardButton>
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography variant="body2">
                                                                    Current turn: <strong>{currentTurnUser.sequenceOrder ?? "—"}. {currentTurnUser.userName ?? currentTurnUser.userId}</strong>
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Available: {currentUserAvailableVolume} | Selected: {pendingTurnVolume} | Remaining: {currentUserRemainingVolume}
                                                                </Typography>
                                                                {currentUserBauLeftEntries.length > 0 ? (
                                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                                        BAU left: {renderBauLeftInline(currentUserBauLeftEntries)}
                                                                    </Typography>
                                                                ) : null}
                                                                {!isAuctionCompleted ? (
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                                                        {isLastCustomerInRound && isLastRound
                                                                            ? "Up next: auction completes after this turn"
                                                                            : <>Up next: <strong>{nextTurnUser?.sequenceOrder ?? "—"}. {nextTurnUser?.userName ?? nextTurnUser?.userId}</strong> (Round {nextTurnRound})
                                                                                {nextTurnUserPresence != null ? <> — <Box component="span" sx={{ fontWeight: 700, color: presenceColor(nextTurnUserPresence) }}>{presenceLabel(nextTurnUserPresence)}</Box></> : null}</>}
                                                                    </Typography>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                </StandardPanel>
                            </Grid2>
                            ) : null}

                    <Grid2 size={{ xs: 12 }}>
                        <StandardPanel title={isCustomerUser ? "My Turn & Round Usage" : "User Sequence & Round Usage"} loading={loading.users} height="320px">
                            {!loading.users && visibleUsers.length === 0 ? (
                                <MessageNotification message={isCustomerUser ? "You are not registered on this annual catalogue." : "No users available for sequence."} />
                            ) : (
                                <List disablePadding>
                                    {visibleUsers.map((user) => {
                                        const index = users.findIndex((u) => u.id === user.id);
                                        const usage = getRoundUsage(user);
                                        const isCurrent = index === currentTurnIndex;
                                        const selectedItems = usage.selectedItemIds.map((id) => {
                                            const row = catalogueItemsById.get(id);
                                            return {
                                                id,
                                                forestCode: row?.forestCode ?? "—",
                                                lotNo: row?.lotNo ?? id,
                                                product: row?.product ?? "Unknown item",
                                                volume: row?.totalVolume ?? 0,
                                            };
                                        });
                                        return (
                                            <ListItem
                                                key={user.id}
                                                disablePadding
                                                sx={{
                                                    py: 0.5,
                                                    px: 1,
                                                    border: "1px solid",
                                                    borderColor: isCurrent ? "primary.main" : "divider",
                                                    borderRadius: 1,
                                                    backgroundColor: isCurrent ? "action.selected" : "transparent",
                                                }}
                                            >
                                                <ListItemText
                                                    primary={`${user.sequenceOrder ?? "—"}. ${user.userName ?? user.userId}${isCurrent ? " (Current)" : ""}`}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Selecting: {usage.selectedItemIds.length} item(s) • Used: {usage.used} • Allowance: {usage.allowance} • Left: {usage.remaining}
                                                            </Typography>
                                                            {selectedItems.length === 0 ? (
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    Current selection: None
                                                                </Typography>
                                                            ) : (
                                                                <Box sx={{ mt: 0.5, display: "grid", gap: 0.5 }}>
                                                                    {selectedItems.map((item) => (
                                                                        <Box
                                                                            key={`${user.id}-${item.id}`}
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: 0.5,
                                                                            }}
                                                                        >
                                                                            {canSelectItems ? (
                                                                                <Tooltip title={isCurrent ? "Remove from current selection" : "Locked/previous round"}>
                                                                                    <span>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            disabled={!isAuctionStarted || !isCurrent || isPendingForTurnAdvance || (isCustomerUser && !isCurrentClientTurn)}
                                                                                            onClick={() => togglePendingItem(item.id)}
                                                                                        >
                                                                                            <Icon fontSize="small">delete</Icon>
                                                                                        </IconButton>
                                                                                    </span>
                                                                                </Tooltip>
                                                                            ) : null}
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Lot {item.lotNo} • BAU {item.forestCode} • {item.product} • Vol {item.volume}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <Tooltip title="Basket by round history">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setBasketDialogUserId(user.id)}
                                                    >
                                                        <Icon fontSize="small">shopping_basket</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </StandardPanel>
                    </Grid2>

                    {!!params.auction?.id && !!activeCatalogueId ? (
                        <Grid2 size={{ xs: 12 }}>
                            {/* Adhoc admin shows both compose + live messages (with remove).
                                Annual was compose-only for admin, so posted messages vanished
                                from the admin view and could not be dismissed. */}
                            {!isMonitorUser ? (
                                <AuctionChat auctionId={params.auction.id} catalogueId={activeCatalogueId} />
                            ) : null}
                            <AuctionMessages catalogueId={activeCatalogueId} />
                        </Grid2>
                    ) : null}
                        </Grid2>
                    </Grid2>

                    <Grid2 size={{ xs: 3 }} sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "grid", gap: 1 }}>
                            <AuctionAnnualCountdown timerAnchors={timerAnchors} />
                            <AuctionUserList
                                catalogueId={activeCatalogueId ?? undefined}
                                onlineUsers={onlineUsers}
                                lastSeenByUserId={heartbeats}
                                tabVisibleByUserId={tabVisibleByUserId}
                            />
                        </Box>
                    </Grid2>
                </Grid2>
            </Grid2>

            {(isAdminUser || isMonitorUser) ? (
                <Grid2 size={{ xs: 12 }}>
                    <StandardPanel title="Basket Summary" loading={false}>
                        {users.length === 0 ? (
                            <MessageNotification message="No users to summarize." />
                        ) : (
                            <List disablePadding>
                                {users.map((user) => {
                                    const basketIds = basketByUserId[user.id] ?? [];
                                    const totalVolume = getUserBasketVolume(user.id);
                                    const userHistory = basketHistoryByUserId[user.id] ?? [];
                                    const currentAllowance = Number(user.volumePerRound ?? 0) + Number(carryOverByUserId[user.id] ?? 0);
                                    const bauLeftEntries = getUserBauLeftEntries(user, true);
                                    return (
                                        <ListItem
                                            key={user.id}
                                            disablePadding
                                            sx={{
                                                py: 0.75,
                                                px: 1,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                mb: 0.75,
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {user.userName ?? user.userId}
                                                        </Typography>
                                                        <Typography variant="body1" color="text.secondary">
                                                            {basketIds.length} item(s) • Vol {totalVolume}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 0.5, display: "grid", gap: 0.75 }}>
                                                        <Typography variant="body1" color="text.secondary" display="block">
                                                            Current allowance: {currentAllowance}
                                                            {bauLeftEntries.length > 0 ? (
                                                                <>
                                                                    {" • BAU left: "}
                                                                    {renderBauLeftInline(bauLeftEntries)}
                                                                </>
                                                            ) : null}
                                                        </Typography>
                                                        {userHistory.length === 0 ? (
                                                            <Typography variant="body1" color="text.secondary" display="block">
                                                                No basket history yet.
                                                            </Typography>
                                                        ) : (
                                                            <>
                                                                {userHistory.map((entry, idx) => {
                                                                    const entryItems = entry.itemIds.map((id) => {
                                                                        const row = catalogueItemsById.get(id);
                                                                        return {
                                                                            key: `${user.id}-${entry.roundNumber}-${id}`,
                                                                            label: `Lot ${row?.lotNo ?? id} • BAU ${row?.forestCode ?? "—"} • ${row?.product ?? "Unknown item"} • Vol ${row?.totalVolume ?? 0}`,
                                                                        };
                                                                    });
                                                                    return (
                                                                        <Box key={`${user.id}-round-${entry.roundNumber}-${idx}`}>
                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                                <Typography
                                                                                    variant="body1"
                                                                                    sx={{
                                                                                        fontWeight: 700,
                                                                                        px: 1,
                                                                                        py: 0.25,
                                                                                        borderRadius: 1,
                                                                                        backgroundColor: "action.selected",
                                                                                    }}
                                                                                >
                                                                                    Round {entry.roundNumber}
                                                                                </Typography>
                                                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                                                    {entry.itemIds.length} item(s)
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="body1" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                                                                Used: {entry.used} • Allowance: {entry.allowance} • Left: {entry.remaining}
                                                                            </Typography>
                                                                            {entryItems.length > 0 ? (
                                                                                <Box sx={{ mt: 0.25, display: "grid", gap: 0.25 }}>
                                                                                    {entryItems.map((item) => (
                                                                                        <Typography key={item.key} variant="body1" color="text.secondary" display="block">
                                                                                            {item.label}
                                                                                        </Typography>
                                                                                    ))}
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography variant="body1" color="text.secondary" display="block">
                                                                                    Items: None
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </StandardPanel>
                </Grid2>
            ) : null}

            <Dialog
                open={basketDialogUserId != null}
                onClose={() => setBasketDialogUserId(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {basketDialogUser ? `${basketDialogUser.userName ?? basketDialogUser.userId} Basket by Round` : "Basket by Round"}
                </DialogTitle>
                <DialogContent dividers>
                    {basketDialogUser == null ? (
                        <MessageNotification message="No user selected." />
                    ) : (
                        <AuctionAnnualBasketHistoryList
                            history={basketHistoryByUserId[basketDialogUser.id] ?? []}
                            catalogueItemsById={catalogueItemsById}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <StandardButton icon="clear" variant="info" onClick={() => setBasketDialogUserId(null)}>
                        Close
                    </StandardButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={turnActionConfirm != null}
                onClose={() => setTurnActionConfirm(null)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{turnActionTitle}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to {turnActionTitle.toLowerCase()}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <StandardButton
                        icon="clear"
                        variant="info"
                        onClick={() => setTurnActionConfirm(null)}
                    >
                        Cancel
                    </StandardButton>
                    <StandardButton
                        icon="check"
                        variant={turnActionConfirm?.type === "accept" ? "success" : "primary"}
                        loading={turnRequestLoading}
                        disabled={!currentTurnUser || !turnActionConfirm || turnRequestLoading}
                        onClick={async () => {
                            if (!turnActionConfirm) return;
                            const completed = await completeTurnServer(turnActionConfirm.skipOnly);
                            if (completed) {
                                setTurnActionConfirm(null);
                            }
                        }}
                    >
                        Confirm
                    </StandardButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
